/**
 * Hook for managing drag-and-drop file attachments over the chat area.
 * Uses a counter to track nested drag enter/leave events and prevent flicker.
 */
import type { DragEvent } from 'react'
import { useCallback, useRef, useState } from 'react'

interface UseChatDropZoneProps {
  onFilesSelected: (fileList: File[]) => void
}

interface DropZoneEventHandlerList {
  onDragEnter: (e: DragEvent) => void
  onDragOver: (e: DragEvent) => void
  onDragLeave: (e: DragEvent) => void
  onDrop: (e: DragEvent) => void
}

export interface UseChatDropZoneReturn {
  isDragOver: boolean
  dropZoneEventHandlerList: DropZoneEventHandlerList
}

const hasFiles = (e: DragEvent): boolean => {
  if (!e.dataTransfer?.types) return false
  return e.dataTransfer.types.includes('Files')
}

export const useChatDropZone = ({
  onFilesSelected,
}: UseChatDropZoneProps): UseChatDropZoneReturn => {
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounterRef = useRef(0)

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!hasFiles(e)) return
    dragCounterRef.current += 1
    if (dragCounterRef.current === 1) {
      setIsDragOver(true)
    }
  }, [])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) {
      setIsDragOver(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounterRef.current = 0
      setIsDragOver(false)

      const droppedFileList = e.dataTransfer?.files
      if (!droppedFileList || droppedFileList.length === 0) return

      const fileList = Array.from(droppedFileList)
      onFilesSelected(fileList)
    },
    [onFilesSelected]
  )

  return {
    isDragOver,
    dropZoneEventHandlerList: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  }
}
